import React, { useEffect, useState } from "react";
import { TextField, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import CommonModal from "../common/CommonModal";
import { House } from "../../hooks/useHouse";

const formatHouseCode = (raw: string): string =>
  raw.replace(/\s+/g, "").toUpperCase();

const formatHouseNameFromCode = (code: string): string => {
  const c = formatHouseCode(code);
  if (!c) return "";
  return `${c.charAt(0)}${c.slice(1).toLowerCase()} House`;
};

interface HouseFormModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (formData: House) => void;
  isSubmitting?: boolean;
  isEdit?: boolean;
  initialData?: House;
}

const HouseFormModal: React.FC<HouseFormModalProps> = ({
  open,
  handleClose,
  onSubmit,
  isSubmitting = false,
  isEdit = false,
  initialData,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<House>({
    houseName: "",
    houseCode: "",
    houseColor: "#3092e3",
  });

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        const code = formatHouseCode(initialData.houseCode || "");
        setFormData({
          id: initialData.id,
          houseCode: code,
          houseName: initialData.houseName || formatHouseNameFromCode(code),
          houseColor: initialData.houseColor || "#3092e3",
        });
      } else {
        setFormData({
          houseName: "",
          houseCode: "",
          houseColor: "#3092e3",
        });
      }
    }
  }, [open, isEdit, initialData]);

  const handleHouseCodeChange = (value: string) => {
    const code = formatHouseCode(value);
    setFormData((prev) => ({
      ...prev,
      houseCode: code,
      houseName: formatHouseNameFromCode(code),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = formatHouseCode(formData.houseCode || "");
    onSubmit({
      ...(isEdit && formData.id != null ? { id: formData.id } : {}),
      houseCode: code,
      houseName: formatHouseNameFromCode(code),
      houseColor: formData.houseColor?.trim(),
    });
  };

  const isValid =
    !!formatHouseCode(formData.houseCode || "") &&
    !!formData.houseColor?.trim();

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={isEdit ? t("edit_house") || "Edit House" : t("add_house") || "Add House"}
      maxWidth="sm"
      onSubmit={handleSubmit}
      submitLabel={isEdit ? t("update") || "Update" : t("add") || "Add"}
      submitDisabled={!isValid || isSubmitting}
      loading={isSubmitting}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <TextField
          label={t("house_code") || "House Code"}
          value={formData.houseCode || ""}
          onChange={(e) => handleHouseCodeChange(e.target.value)}
          required
          fullWidth
          placeholder={t("enter_house_code_placeholder") || "e.g. blue"}
          inputProps={{ style: { textTransform: "uppercase" } }}
        />
        <TextField
          label={t("house_name") || "House Name"}
          value={formData.houseName || ""}
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
          helperText={t("house_name_auto_generated") || "Auto-generated from house code"}
        />
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label={t("house_color") || "House Color"}
            type="color"
            value={formData.houseColor || "#3092e3"}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, houseColor: e.target.value }))
            }
            sx={{ width: 200 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            value={formData.houseColor || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, houseColor: e.target.value }))
            }
            placeholder="#3092e3"
            fullWidth
          />
        </Box>
      </Box>
    </CommonModal>
  );
};

export default HouseFormModal;
